import { FC, useMemo } from "react";
import classNames from "classnames";
import {
  formatName,
  currencyExchangeText,
  useCurrencyExchanges,
  shipmentStatusFormat,
  paymentModeFormat,
  documentStatusFormat,
} from "../../utils";
import { useAppSelector } from "../../store/hooks";
import moment from "moment";
import {
  DeliveryType,
  DocumentType,
  ShipmentInterface,
  ShipmentStatus,
} from "../../interfaces";
import { useNavigate } from "react-router-dom";

interface PendingShipmentItemProps {
  index: number;
  showLocation?: boolean;
  showDelivery?: boolean;
  deliveryInColumn?: boolean;
  shipment: ShipmentInterface;
}

const PendingShipmentItem: FC<PendingShipmentItemProps> = ({
  index,
  shipment,
  showLocation,
  showDelivery,
  deliveryInColumn,
}) => {
  const navigate = useNavigate();
  const taxIdentificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  );
  const businessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  );

  const exchanges = useCurrencyExchanges();

  const lastBu = useMemo(() => {
    return businessUnits.find((b) => b.code === shipment.lastBU);
  }, [shipment.lastBU, businessUnits]);

  return (
    <tr
      className={classNames(
        "cursor-pointer hover:bg-gray-100",
        index % 2 === 0 && "bg-gray-50"
      )}
      onClick={() => navigate(`/shipment/${shipment.id}`)}
    >
      <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left">
        <p className="text-indigo-600">{shipment.shipmentNumber}</p>
        <p>
          <span className="text-gray-400">Creada: </span>{" "}
          {shipment.createdAt
            ? new Date(shipment.createdAt)
                .toLocaleDateString("es-VE", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace("a", "AM")
                .replace("p", "PM")
                .slice(0, -4)
            : null}
        </p>
        <p>{paymentModeFormat(shipment.paymentMode)}</p>
        <p className="">{shipmentStatusFormat(shipment.status)}</p>
      </td>

      <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left truncate">
        <p>{formatName(shipment.shipper.accountFullName)}</p>
        <p className="text-gray-400 font-medium">
          {
            taxIdentificationTypes.find(
              (t) =>
                t.taxIdentificationTypeId ===
                shipment.shipper.taxIdentificationTypeID
            )?.abreviationName
          }
          {shipment.shipper.identificationNumber}
        </p>
        <p className="text-gray-400 font-medium">
          {shipment.shipper.listAccountPhone[0].phoneNumber}
        </p>
        <p>
          {shipment.buSource.code} - {shipment.buSource.location.name}
        </p>
      </td>

      <td className="text-xs px-4 py-4 font-bold text-gray-500 truncate text-left truncate">
        <p>{formatName(shipment.consignee.accountFullName)}</p>
        <p className="text-gray-400 font-medium">
          {
            taxIdentificationTypes.find(
              (t) =>
                t.taxIdentificationTypeId ===
                shipment.consignee.taxIdentificationTypeID
            )?.abreviationName
          }
          {shipment.consignee.identificationNumber}
        </p>
        <p className="text-gray-400 font-medium">
          {shipment.consignee.listAccountPhone[0].phoneNumber}
        </p>
        <p>
          {shipment.buConsignee?.code} - {shipment.buConsignee?.location.name}
        </p>
      </td>

      <td className="text-xs px-4 py-4 font-normal w-auto text-gray-500 truncate">
        <p className={classNames(deliveryInColumn && "hidden")}>
          Entrega:{" "}
          <span className="font-bold">
            {shipment.pickupType === DeliveryType.AT_HOME
              ? "En Tienda"
              : "A Domicilio"}
          </span>
        </p>
        <p>
          Nº de piezas:{" "}
          <span className="font-bold">{shipment.pieces.length}</span>
        </p>
        <p>
          Peso:{" "}
          <span className="font-bold">{shipment.totalChargedWeight} Kg</span>
        </p>
      </td>

      <td className="text-left text-xs px-4 py-4 font-semibold w-auto text-gray-500 truncate">
        <p className={classNames("font-bold", !shipment.document && "hidden")}>
          <span className="font-normal">
            {shipment.document?.documentTypeID === DocumentType.INVOICE
              ? "Factura: "
              : "Orden: "}
          </span>
          {!!shipment.document
            ? shipment.document.documentNumber
            : "Sin Factura Asociada"}
        </p>
        <p className="font-bold">
          {!!shipment.document
            ? documentStatusFormat(shipment.document.status)
            : "Sin Factura Asociada"}
        </p>

        <p className={classNames("hidden")}>
          Monto de la guía:{" "}
          <span className="font-bold">
            {currencyExchangeText(shipment.total, exchanges, "USD")}
          </span>
        </p>
      </td>

      <td
        className={classNames(
          "text-left text-xs px-4 py-4 font-normal w-auto text-gray-500 truncate",
          !showLocation && "hidden"
        )}
      >
        <p className="font-bold">
          {shipment.status === ShipmentStatus.DELIVERED
            ? "Entregada a cliente"
            : lastBu
            ? `${shipment.lastBU} - ${lastBu?.location.name}`
            : shipment.lastBU}
        </p>
        <p
          className={classNames(
            shipment.status === ShipmentStatus.DELIVERED && "hidden"
          )}
        >
          Desde:{" "}
          <span className="font-bold">
            {shipment.lastBUDate
              ? new Date(shipment.lastBUDate)
                  .toLocaleDateString("es-VE", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                  .replace("a", "AM")
                  .replace("p", "PM")
                  .slice(0, -4)
              : null}
          </span>
        </p>
      </td>

      {deliveryInColumn && (
        <td className="text-center text-sm px-4 py-4 font-semibold w-auto text-gray-500 truncate">
          {moment().diff(moment(shipment.lastBUDate), "days")}
        </td>
      )}

      {showDelivery && (
        <td className="text-xs px-4 py-4 font-semibold w-auto text-gray-500 min-w-[15rem] max-w-[25rem]">
          <div
            className="overflow-hidden"
            style={{
              lineClamp: "3",
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
            }}
          >
            <p>
              {shipment.pickupType === DeliveryType.AT_OFFICE
                ? "En Tienda"
                : shipment.consigneeAddress.name}
            </p>
            <p
              className={classNames(
                !shipment.consigneeAddress.reference && "!hidden"
              )}
            >
              <span className="font-normal">Punto de ref:</span>{" "}
              {shipment.consigneeAddress.reference}
            </p>
          </div>
        </td>
      )}
    </tr>
  );
};

export default PendingShipmentItem;
